import React, { useState, useEffect, useRef, useMemo, useCallback } from'react';
import ReactECharts from 'echarts-for-react';
import useGetPolicyTimeline from '../../hooks/useGetPolicyTimeline';
import { LoadingDiv } from '@components/LoadingDiv';
import { FullScreenTimelineModal } from '../FullScreenTimelineModal';
import util from '@utils/utils';
import { useConfig } from '../../../../contexts/ConfigContext'; // ✅ 添加配置导入

export const Timeline = (props) => {
  const { 
    filePageData,
    currentPolicy, 
    isInModal,
    initialData,
    initialRelatedPolicies,
    initialSelectedRelationships,
    initialSelectedDepartments,
    initialSelectedMonths,
    initialAllMonths,
    mode,
    data: externalData, // 新增：接收外部传入的数据
  } = props;
  
  const { theme } = useConfig(); // ✅ 获取主题配置
  const chartRef = useRef(null); 
  const [parentWidth, setParentWidth] = useState(900); 
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Determine if we should use small mode
  const isSmallMode = mode === 'small';

  useEffect(() => {
    const updateWidth = () => {
      if (chartRef.current) {
        setParentWidth(chartRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);

    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  const relationshipColors = {
    reference: '#FFA500', // Orange
    update: '#008000',    // Green
    implementation: '#8B0000', // Dark Red
    jiedu: '#800080',     // Purple
    xiangguan: '#0000FF'  // Blue
  };

  const [relatedPolicies, setRelatedPolicies] = useState(initialRelatedPolicies || []);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedRelationships, setSelectedRelationships] = useState(initialSelectedRelationships || Object.keys(relationshipColors));
  const [selectedDepartments, setSelectedDepartments] = useState(initialSelectedDepartments || []);
  const [allDates, setAllDates] = useState([]);
  const [departmentPositions, setDepartmentPositions] = useState({});
  const [selectedMonths, setSelectedMonths] = useState(initialSelectedMonths || []);
  const [allMonths, setAllMonths] = useState(initialAllMonths || []);
  const { loading, data: internalData, setQuery } = useGetPolicyTimeline();
  // 使用外部传入的数据，如果没有则使用内部数据
  const data = externalData || internalData;
  const [currentPolicyItem, setCurrentPolicyItem] = useState(currentPolicy);
  
  // ✅ 添加数据处理完成状态
  const [dataProcessed, setDataProcessed] = useState(false);

  useEffect(() => {
    if (currentPolicy && (currentPolicy?.policy_title || currentPolicy?.policy_name)) {
      if(mode === 'small') {
        // 在 small 模式下，如果没有 filePageData，也调用接口
        setQuery({
          policy_id: currentPolicy?.source_id,
          policy_title: currentPolicy?.policy_title || currentPolicy?.policy_name,
        });
      } else {
        setQuery({
          policy_id: currentPolicy?.source_id,
          policy_title: currentPolicy?.policy_title || currentPolicy?.policy_name,
        });
      }
    }
  }, [currentPolicy?.policy_title, currentPolicy?.policy_name, isInModal, filePageData, mode]);

  useEffect(() => {
    if (data && data?.length > 0) {
      const _currentPolicyItem = data?.find(i => i?.source_id === currentPolicy?.source_id) ?? currentPolicy;
      setCurrentPolicyItem(_currentPolicyItem);
      // 兼容不同的数据结构：policy_timeline 接口有 score 字段，policy_timeline&mind_map 接口没有
      let _data = data?.filter(i => {
        // 如果有 score 字段，使用原来的过滤逻辑
        if (i?.score !== undefined) {
          return i?.score > 90 && i?.source_id !== currentPolicy?.source_id;
        }
        // 如果没有 score 字段，直接过滤掉当前政策
        return i?.source_id !== currentPolicy?.source_id;
      })?.slice(0, 6);
      
      if(_data?.length === 0){
        _data = data?.filter(i => i?.source_id !== currentPolicy?.source_id)?.slice(0, 2);
      }
      const arr = _data?.map(item => {
        // 兼容不同的数据结构：policy_timeline 接口有 score 和 policy_class 字段，policy_timeline&mind_map 接口没有
        const hasScore = item?.score !== undefined;
        const hasPolicyClass = item?.policy_class !== undefined;
        
        if (
          item?.from_organization?.includes(_currentPolicyItem?.from_organization)
          && (!hasPolicyClass || !item?.policy_class?.includes('解读'))
          && (!hasScore || item?.score > 99)
          && new Date(item?.policy_time) > new Date(_currentPolicyItem?.policy_time)
        ) {
          return { ...item, relationship: 'update' };
        } else if (
          !item?.from_organization?.includes(_currentPolicyItem?.from_organization)
          && (!hasPolicyClass || !item?.policy_class?.includes('解读'))
        ) {
          return { ...item, relationship: 'reference' };
        } else if (
          (!hasPolicyClass || (!item?.policy_class?.includes('解读') && !item?.policy_class?.includes('国务院') && !item?.policy_class?.includes('中央') && !item?.policy_class?.includes('重要讲话')))
        ) {
          return { ...item, relationship: 'implementation' };
        } else {
          return { ...item, relationship: 'jiedu' };
        }
      })?.filter(Boolean);
      
      setRelatedPolicies(arr || []);
      
      // ✅ 标记数据处理完成
      setDataProcessed(true);
      
      const allDepartments = [...new Set(arr.map(p => p.from_organization))];
      setSelectedDepartments(allDepartments);

      const allPolicies = [_currentPolicyItem, ...arr];
      const dates = allPolicies.map(p => p && p.policy_time ? new Date(p.policy_time).getTime() : 0);
      setAllDates([Math.min(...dates), Math.max(...dates)]);

      const departments = [...new Set(allPolicies.map(p => p.from_organization ?? '国务院'))];
      const departmentHeight = 400;
      const positions = {};
      departments.forEach((dept, index) => {
        positions[dept] = (index + 1) * (departmentHeight / (departments.length + 1));
      });
      setDepartmentPositions(positions);

      const months = [...new Set(allPolicies.map(p => {
        const date = new Date(p.policy_time);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      }))].sort();
      setAllMonths(months);
      setSelectedMonths(months);
    }
  }, [data, isInModal]);

  const handleRelationshipClick = (relationship) => {
    if (selectedRelationships.includes(relationship)) {
      setSelectedRelationships(selectedRelationships.filter(r => r!== relationship));
    } else {
      setSelectedRelationships([...selectedRelationships, relationship]);
    }
  };

  const handleDepartmentClick = (department) => {
    if (selectedDepartments.includes(department)) {
      setSelectedDepartments(selectedDepartments.filter(d => d!== department));
    } else {
      setSelectedDepartments([...selectedDepartments, department]);
    }
  };

  const handleMonthClick = (month) => {
    if (selectedMonths.includes(month)) {
      setSelectedMonths(selectedMonths.filter(m => m!== month));
    } else {
      setSelectedMonths([...selectedMonths, month]);
    }
  };

  const getFilteredPolicies = () => {
    // 如果没有筛选条件，返回所有相关政策
    if (selectedDepartments.length === 0 && selectedMonths.length === 0) {
      return relatedPolicies.filter(policy =>
        selectedRelationships.includes(policy.relationship)
      );
    }
    
    return relatedPolicies.filter(policy =>
      selectedRelationships.includes(policy.relationship) &&
      (selectedDepartments.length === 0 || selectedDepartments.includes(policy.from_organization)) &&
      (selectedMonths.length === 0 || selectedMonths.includes(new Date(policy.policy_time).getFullYear() + '-' + String(new Date(policy.policy_time).getMonth() + 1).padStart(2, '0')))
    );
  };

  const getRelationshipLabel = (relationship) => {
    return relationship === 'reference' ? '引用' :
      relationship === 'update' ? '更新' :
      relationship === 'implementation' ? '实施' :
      relationship === 'jiedu' ? '解读' : '相关';
    
  };

  const getChartOption = () => {
    const filteredRelatedPolicies = getFilteredPolicies();
    const allPolicies = [currentPolicyItem, ...filteredRelatedPolicies];
    
    // 调试信息已移除
    
    const dates = allPolicies.map(p => p && p.policy_time ? new Date(p.policy_time).getTime() : 0);
    const minDate = Math.min(...dates);
    const maxDate = Math.max(...dates);
    const dateRange = maxDate - minDate;

    const filteredDepartments = [...new Set(allPolicies.map(p => p.from_organization))];
    const departmentToPosition = {};
    filteredDepartments.forEach((dept, index) => {
      departmentToPosition[dept] = index;
    });
    const departmentCounts = {};
    allPolicies.forEach(policy => {
      const department = policy.from_organization;
      if (!departmentCounts[department]) {
        departmentCounts[department] = 0;
      }
      departmentCounts[department]++;
    });

    const nodes = allPolicies.map((policy,index) => {
      const timePosition = (new Date(policy.policy_time).getTime() - minDate) / dateRange;
      const isCurrent = policy.source_id === currentPolicy.source_id;
      const department = policy.from_organization;
      const baseYPosition = departmentToPosition[department];
      const nodeCount = departmentCounts[department];
      const departmentPolicies = allPolicies.filter(p => p.from_organization === department);
      const departmentIndex = departmentPolicies.findIndex(p => p.source_id === policy.source_id);
      const offset = departmentIndex - (nodeCount - 1) / 2;
      const yPosition = baseYPosition + offset * 0.1;

      return {
        id: policy.source_id,
        name: policy.policy_title || policy.policy_name,
        policyTime: policy.policy_time,
        fromOrganization: policy.from_organization,
        value: [
          timePosition * parentWidth,
          yPosition
        ],
        symbolSize: isCurrent? 30 : 20,
        symbolOffset: [0, offset],
        itemStyle: {
          color: isCurrent? '#2C7A7B' : '#506bbd',
          borderColor: '#fff',
          borderWidth: 2,
          shadowBlur: 10,
          shadowColor: 'rgba(0, 0, 0, 0.3)'
        },
        label: {
          show: true, // 显示标签
          formatter: [
            `{title|${policy.policy_title || policy.policy_name}}`,
          ].join('\n'),
          rich: {
            title: {
              color: '#333',
              fontSize: isSmallMode ? 10 : 12,
              fontWeight: 'bold',
              width: isSmallMode ? 150 : 200,
              overflow: 'break',
              ellipsis: true
            },
          },
          position: 'right',
          distance: 5,
          offset:[0, offset * (isInModal ? 40 : 20)]
        }
      };
    });

    const links = filteredRelatedPolicies.map(policy => ({
      source: new Date(policy.policy_time) > new Date(currentPolicyItem.policy_time)? currentPolicyItem.source_id : policy.source_id,
      target: new Date(policy.policy_time) > new Date(currentPolicyItem.policy_time)? policy.source_id : currentPolicyItem.source_id,
      lineStyle: {
        color: relationshipColors[policy.relationship],
        width: 2,
        curveness: 0.2,
        type: [8, 8]
      },
      label: {
        show: true, // 显示连接线标签
        formatter:
          policy.relationship ==='reference'? '引用' :        
            policy.relationship === 'update'? '更新' :
              policy.relationship === 'implementation'? '实施' :
                policy.relationship === 'jiedu'? '解读' : '相关',
        fontSize: isSmallMode ? 10 : 12,
        color: '#666'
      }
    }));

    return {
      grid: {
        left: '15%', 
        right: '33%', 
        bottom: '15%',
        top: '15%'
      },
      xAxis: {
        show: true,
        type: 'value',
        min: 0,
        max: parentWidth,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          show: true, // 显示坐标轴标签
          formatter: (value) => {
            const date = new Date(minDate + (value / parentWidth) * dateRange);
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          }
        },
        splitLine: {
          show: true, // 显示分割线
          lineStyle: {
            type: 'dashed',
            color: '#eee'
          }
        }
      },
      yAxis: {
        show: true,
        type: 'category',
        data: filteredDepartments,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          show: true, // 显示坐标轴标签
          rich: {
            dept: {
              width: 100,
              overflow: 'break',
              ellipsis: true,
              color: '#333',
              fontSize: 12
            }
          }
        },
        splitLine: {
          show: true, // 显示分割线
          lineStyle: {
            type: 'dashed',
            color: '#eee'
          }
        }
      },
      series: [
        {
          type: 'graph',
          layout: 'none',
          coordinateSystem: 'cartesian2d',
          symbolSize: 40,
          roam: true, // 启用缩放和拖拽
          label: {
            show: true
          },
          edgeSymbol: ['circle', 'arrow'],
          edgeSymbolSize: [4, 10],
          data: nodes,
          links: links,
          emphasis: {
            focus: 'adjacency',
            lineStyle: {
              width: 4
            }
          },
          animation: true,
          force: {
            repulsion: 100,
            edgeLength: 200
          }
        }
      ],
      animation: true,
      animationDuration: 1500,
      animationEasingUpdate: 'quinticInOut'
    };
  };

  // Add a function to render the timeline list for small mode
  const renderTimelineList = () => {
    const filteredPolicies = getFilteredPolicies();
    // Sort all policies by date (newest first)
    const allPolicies = [currentPolicyItem, ...filteredPolicies].sort((a, b) => 
      new Date(a.policy_time).getTime() - new Date(b.policy_time).getTime()
    );
    
    return (
      <ul className="flex-1 timeline timeline-snap-icon max-md:timeline-compact timeline-vertical my-timeline">
        {allPolicies.map((item,index) => (
          <li key={item.source_id}>
            <div className="timeline-middle">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
                <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-11.25a.75.75 0 0 0-1.5 0v4.59L7.3 9.24a.75.75 0 0 0-1.1 1.02l3.25 3.5a.75.75 0 0 0 1.1 0l3.25-3.5a.75.75 0 1 0-1.1-1.02l-1.95 2.1V6.75Z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="timeline-end mb-10">
              <time className="font-mono italic">{item?.policy_time?.split(' ')[0] ?? item?.policy_time} {item?.from_organization}</time>
              <div 
                className="text-lg font-black link hover:text-primary" 
                onClick={() => window.open(util.buildPolicyFileUrlDirect(item?.policy_title || item?.policy_name, item?.source_id, '', window.location.href), '_blank')}>
                {item?.policy_title || item?.policy_name}
              </div>
            </div>
            {index !== allPolicies.length - 1 && <hr/>}
          </li>
        ))}
      </ul>
    );
  };
  // 使用useMemo缓存图表选项
  const chartOption = useMemo(() => getChartOption(), [
    parentWidth, 
    selectedRelationships, 
    selectedDepartments, 
    selectedMonths, 
    relatedPolicies, 
    currentPolicy
  ]);
  
  // 使用useCallback包装事件处理函数
  const handleChartEvents = useCallback({
    'click': (event) => {
      const { componentType, seriesType, dataType, data } = event;
      if (componentType === 'series' && seriesType === 'graph' && dataType === 'node') {
        window.open(`${process.env.REACT_APP_DOMAIN}policy/file?fileName=${encodeURIComponent(data?.name)}&fileId=${data?.id}&returnUrl=${encodeURIComponent(window.location.href)}`, '_blank');
      }
    }
  }, []);

  // 检查是否有有效的时间线数据
  const hasValidTimelineData = () => {
    const filteredPolicies = getFilteredPolicies();
    return filteredPolicies && filteredPolicies.length > 0;
  };

  // ✅ 渲染空状态提示
  const renderEmptyState = () => {
    // 检查是否有任何筛选条件被应用
    const hasActiveFilters =
      selectedRelationships.length < Object.keys(relationshipColors).length ||
      selectedDepartments.length < [...new Set(relatedPolicies.map(p => p.from_organization))].length ||
      selectedMonths.length < allMonths.length;

    // 检查原始数据中是否有相关政策
    const hasOriginalRelatedPolicies = relatedPolicies && relatedPolicies.length > 0;

    // 只有在有活动筛选条件且原始数据存在时才显示重置按钮
    const shouldShowResetButton = hasActiveFilters && hasOriginalRelatedPolicies;

    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div
            className="text-lg font-medium mb-2"
            style={{ color: theme.primary }}
          >
            {hasActiveFilters ?
              (hasOriginalRelatedPolicies ? '此条件下没有相关政策' : '此政策没有相关的时间线分析，请看看其他的吧。') :
              '此政策没有相关的时间线分析，请看看其他的吧。'
            }
          </div>
          <div className="text-sm text-gray-500">
            {hasActiveFilters ?
              (hasOriginalRelatedPolicies ? '请尝试调整筛选条件后重试' : '暂未找到与此政策相关的其他政策文件') :
              '暂未找到与此政策相关的其他政策文件'
            }
          </div>
          {shouldShowResetButton && (
            <div className="mt-4">
              <button
                className="btn btn-sm btn-outline"
                onClick={() => {
                  // 重置所有筛选条件
                  setSelectedRelationships(Object.keys(relationshipColors));
                  setSelectedDepartments([...new Set(relatedPolicies.map(p => p.from_organization))]);
                  setSelectedMonths(allMonths);
                }}
              >
                重置筛选条件
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ✅ 简化渲染逻辑，直接在最终渲染时判断
  return (
    <>
      {!isInModal && (
        <FullScreenTimelineModal
          currentPolicy={currentPolicyItem}
          isOpen={isFullScreen}
          onClose={() => {
            setIsFullScreen(false);
          }}
          initialData={data}
          initialRelatedPolicies={relatedPolicies}
          initialSelectedRelationships={selectedRelationships}
          initialSelectedDepartments={selectedDepartments}
          initialSelectedMonths={selectedMonths}
          initialAllMonths={allMonths}
        />
      )}
      <div className="relative">
        {loading && (
          <div className="flex items-center justify-center p-4">
            <LoadingDiv />
          </div>
        )}
        {!loading && !hasValidTimelineData() && renderEmptyState()}
        {!loading && hasValidTimelineData() && (
          <div className={`${isSmallMode ? 'p-4' : 'p-8'}`} ref={chartRef}>
            <div className="flex justify-between">
              {/* Show all filters in both modes */}
              <div className="flex-1">
                {/* Policy relationship filter - show in both modes */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-6">
                    <span className="text-sm font-medium no-translate">政策关系：</span>
                    {Object.entries(relationshipColors).map(([relation, color]) => (
                      <div
                        key={relation}
                        className="flex items-center gap-2 cursor-pointer"
                        onClick={() => handleRelationshipClick(relation)}
                      >
                        <span
                          className="w-8 h-1"
                          style={{ 
                            backgroundColor: color, 
                            opacity: selectedRelationships.includes(relation) ? 1 : 0.3,
                            borderStyle: 'dashed',
                            borderWidth: 1,
                            borderColor: color,
                            borderDash: [5, 5]
                          }}
                        />
                        <span className={`text-sm no-translate ${selectedRelationships.includes(relation) ? '' : 'text-secondary'}`}>
                          {getRelationshipLabel(relation)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Department filter - show in both modes */}
                <div className={`flex flex-wrap ${isSmallMode ? '' : 'items-center'} gap-6 mb-6`}>
                  <span className="text-sm font-medium no-translate">政策来源：</span>
                  {[...new Set(relatedPolicies.map(p => p.from_organization))].map(department => (
                    <div
                      key={department}
                      className="flex items-center gap-2 cursor-pointer"
                      onClick={() => handleDepartmentClick(department)}
                    >
                      <input 
                        type="checkbox" 
                        className="checkbox checkbox-sm" 
                        checked={selectedDepartments.includes(department)} 
                      />
                      <span className={`text-sm ${selectedDepartments.includes(department) ? '' : 'text-secondary'}`}>{department}</span>
                    </div>
                  ))}
                </div>
                
                {/* Time dimension filter - show in both modes */}
                <div className={`flex flex-wrap ${isSmallMode ? '' : 'items-center'} gap-6 mb-6`}>
                <span className="text-sm font-medium no-translate">时间维度：</span>
                  {allMonths.map(month => (
                    <div
                      key={month}
                      className="flex items-center gap-2 cursor-pointer"
                      onClick={() => handleMonthClick(month)}
                    >
                      <input 
                        type="checkbox" 
                        className="checkbox checkbox-sm" 
                        checked={selectedMonths.includes(month)} 
                      />
                      <span className={`text-sm ${selectedMonths.includes(month) ? '' : 'text-secondary'}`}>{month}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Fullscreen button - show in both modes */}
              {!isInModal && (
                <div className="flex justify-end mb-4">
                  <button
                    className="btn btn-sm"
                    onClick={() => setIsFullScreen(true)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                    </svg>
                    全屏
                  </button>
                </div>
              )}
            </div>

            {/* Always render ECharts for both modes */}
            <div className={`${isInModal ? 'h-[calc(90vh-250px)]' : isSmallMode ? 'h-[400px]' : 'h-[400px]'}`}>
              <ReactECharts
                option={chartOption}
                style={{ height: '100%' }}
                opts={{ renderer: 'svg', notMerge: false }}
                onEvents={handleChartEvents}
                lazyUpdate={true}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};